/**
 * Where a vanity's cabinetry runs along its wall: `{ center, width }`.
 *
 * For a vanity assembled as one cabinet, that is the cabinet. A vanity
 * rebuilt as a cabinet run (every vanity built in a cabinet line) is several
 * boxes — drawer stacks, fillers and one or two sink bases — and its body, a
 * sink base, is only part of it. Measuring mirrors and lights from the body
 * sized them for the 60" base of an 89" vanity, or for one half of a double
 * whose bowls each got their own base.
 *
 * Measured along the BODY's own axis from each box's rotated footprint
 * (`footprintFor`, local +X at `(cos ry, -sin ry)`), so a box a few degrees
 * off still projects correctly. For a single cabinet this is exactly its
 * centre and its `dimensions.x`.
 *
 * Pure.
 */
const { footprintFor } = require('../geometry/objectFootprint');

/**
 * @param {Object} body - the vanity's body (`getVanityCabinet`)
 * @param {Object[]} boxes - the vanity's lower boxes; the body is included
 *   whether or not it is in the list
 * @returns {{ center: {x,y,z}, width: number }|null} null when nothing has
 *   a usable footprint, so callers keep their own fallback
 */
function vanityExtent(body, boxes = []) {
  if (!body?.center) return null;
  const ry = body.rotation?.y || 0;
  const axis = { x: Math.cos(ry), z: -Math.sin(ry) };
  const along = (p) =>
    (p.x - body.center.x) * axis.x + (p.z - body.center.z) * axis.z;

  let lo = Infinity;
  let hi = -Infinity;
  for (const box of new Set([body, ...boxes])) {
    const corners = box?.dimensions
      ? footprintFor(
          box.center,
          box.dimensions.x,
          box.dimensions.z,
          box.rotation?.y || 0
        )
      : null;
    for (const c of corners || []) {
      const t = along(c);
      lo = Math.min(lo, t);
      hi = Math.max(hi, t);
    }
  }
  if (!(hi > lo)) return null;

  const mid = (lo + hi) / 2;
  return {
    center: {
      ...body.center,
      x: body.center.x + axis.x * mid,
      z: body.center.z + axis.z * mid,
    },
    width: hi - lo,
  };
}

module.exports = { vanityExtent };
